import { normalizeTournamentData } from '@sportal365/smp-widget-normalizers';
import { ITeamSquadConfig, ITeamSquadItem, ITeamSquadResponse } from '../../../../packages/types';
import { NOT_AVAILABLE } from '../../../../packages/constants/notAvailable';
import {
  getDefaultShortValue,
  getLabelValue,
  LinkCreationService,
  removeUndefinedPropertiesFromObject,
  toSentenceCase,
} from '../../../../packages/utils';
import { INormalizedTeamSquadData, INormalizedTeamSquadItem, TeamPlayerElement } from '../types';
import { normalizeTeamSquadGridItem } from './normalizeTeamSquadGridItem';
import { normalizeTeamSquadItem } from './normalizeTeamSquadItem';
import { TARGET_BLANK, TARGET_SELF } from '../../../../packages/constants/windowTarget';
import { DEFAULT_TEAM_SQUAD_DATA_ELEMENTS } from '../../../../packages/constants/defaultDataElements';
import { TEAM_ENTITY } from '../../../../packages/constants/entityTypes';
import { convertElementToPlayerValue } from '../utils/convertElementToPlayerValue';

interface INormalizeTeamSquadDataArgs {
  squadData: ITeamSquadResponse[];
  dataLabels?: ITeamSquadConfig['dataLabels'];
  dataEntityLinks?: ITeamSquadConfig['dataEntityLinks'];
  dataTeamNameType?: ITeamSquadConfig['dataTeamNameType'];
  dataElements?: ITeamSquadConfig['dataElements'];
  displayImage?: ITeamSquadConfig['dataImageDisplay'];
}

export const normalizeTeamSquadData = ({
  squadData,
  dataLabels,
  dataEntityLinks,
  dataTeamNameType,
  dataElements,
  displayImage,
}: INormalizeTeamSquadDataArgs): INormalizedTeamSquadData | undefined => {
  if (!squadData[0]) return;

  const linkService = dataEntityLinks && new LinkCreationService(dataEntityLinks);

  const teamSquad = squadData?.reduce((output: ITeamSquadResponse, data: ITeamSquadResponse) => {
    return data;
  });

  const squad = teamSquad.items.reduce((output: ITeamSquadItem, data: ITeamSquadItem) => {
    return data;
  });

  const statFields =
    !dataElements || !Array.isArray(dataElements) || !dataElements.length ? DEFAULT_TEAM_SQUAD_DATA_ELEMENTS : dataElements;

  const playerStats = statFields.map((field) => convertElementToPlayerValue(field as TeamPlayerElement));
  const statsColumns = statFields.map((field) => ({
    value: getLabelValue(dataLabels && dataLabels[`${field}_short`], getDefaultShortValue(field)),
    statValue: field,
    tooltip: getLabelValue(dataLabels && dataLabels[field], toSentenceCase(field).toUpperCase()),
    css: { textOverflow: 'ellipsis', overflow: 'hidden', width: '$6', textAlign: 'center' },
  }));

  const goalkeeperStatsFields = Array.from(new Set([...statFields]));
  const goalkeeperStats = goalkeeperStatsFields.map((field) => convertElementToPlayerValue(field as TeamPlayerElement));
  const goalkeeperStatsColumns = goalkeeperStatsFields.map((field) => ({
    value: getLabelValue(dataLabels && dataLabels[`${field}_short`], getDefaultShortValue(field)),
    tooltip: getLabelValue(dataLabels && dataLabels[field], toSentenceCase(field).toUpperCase()),
    css: { textOverflow: 'ellipsis', overflow: 'hidden', width: '$6', textAlign: 'center' },
  }));

  const defenderPlayers = teamSquad.items
    .filter((item) => item.position === 'defender')
    .map((item) =>
      normalizeTeamSquadGridItem({
        item,
        dataEntityLinks,
        dataLabels,
        dataTeamNameType,
        playerStats,
        tooltips: statsColumns.map((col) => col.tooltip),
      }),
    );

  const keeperPlayers = teamSquad.items
    .filter((item) => item.position === 'keeper')
    .map((item) =>
      normalizeTeamSquadGridItem({
        item,
        dataEntityLinks,
        dataLabels,
        dataTeamNameType,
        playerStats,
        goalkeeperStats,
        tooltips: goalkeeperStatsColumns.map((col) => col.tooltip),
      }),
    );

  const midfielderPlayers = teamSquad.items
    .filter((item) => item.position === 'midfielder')
    .map((item) =>
      normalizeTeamSquadGridItem({
        item,
        dataEntityLinks,
        dataLabels,
        dataTeamNameType,
        playerStats,
        tooltips: statsColumns.map((col) => col.tooltip),
      }),
    );

  const forwardPlayer = teamSquad.items
    .filter((item) => item.position === 'forward')
    .map((item) =>
      normalizeTeamSquadGridItem({
        item,
        dataEntityLinks,
        dataLabels,
        dataTeamNameType,
        playerStats,
        tooltips: statsColumns.map((col) => col.tooltip),
      }),
    );

  const filteredDefenderPlayers = teamSquad.items
    .filter((item) => item.position === 'defender')
    .map((item) => normalizeTeamSquadItem({ item, dataEntityLinks, dataTeamNameType, playerStats, displayImage }));

  const filteredKeeperPlayers = teamSquad.items
    .filter((item) => item.position === 'keeper')
    .map((item) =>
      normalizeTeamSquadItem({
        item,
        dataEntityLinks,
        dataTeamNameType,
        playerStats,
        goalkeeperStats,
        displayImage,
      }),
    );

  const filteredMidfielderPlayers = teamSquad.items
    .filter((item) => item.position === 'midfielder')
    .map((item) => normalizeTeamSquadItem({ item, dataEntityLinks, dataTeamNameType, playerStats, displayImage }));

  const filteredForwardPlayers = teamSquad.items
    .filter((item) => item.position === 'forward')
    .map((item) => normalizeTeamSquadItem({ item, dataEntityLinks, dataTeamNameType, playerStats, displayImage }));

  const columns = [{ value: getLabelValue(dataLabels?.name, 'NAME'), css: { flexGrow: '1' } }, ...statsColumns];

  const goalkeeperColumns = [
    {
      value: getLabelValue(dataLabels?.name, 'NAME'),
      css: { flexGrow: '1' },
    },
    ...goalkeeperStatsColumns,
  ];

  const positionLabels = {
    goalkeeper: getLabelValue(dataLabels?.goalkeepers, 'GOALKEEPERS'),
    defender: getLabelValue(dataLabels?.defenders, 'DEFENDERS'),
    midfielder: getLabelValue(dataLabels?.midfielders, 'MIDFIELDERS'),
    forward: getLabelValue(dataLabels?.forwards, 'FORWARDS'),
  };

  return removeUndefinedPropertiesFromObject({
    items: {
      keepers: {
        data: [
          {
            name: positionLabels.goalkeeper,
            item: {
              data: filteredKeeperPlayers as INormalizedTeamSquadItem,
              desktopColumns: goalkeeperColumns,
              mobileColumns: goalkeeperColumns,
            },
            position: 'keeper',
          },
        ],
      },
      defenders: {
        data: [
          {
            name: positionLabels.defender,
            item: {
              data: filteredDefenderPlayers as INormalizedTeamSquadItem,
              desktopColumns: columns,
              mobileColumns: columns,
            },
            position: 'defender',
          },
        ],
      },
      midfielders: {
        data: [
          {
            name: positionLabels.midfielder,
            item: {
              data: filteredMidfielderPlayers as INormalizedTeamSquadItem,
              desktopColumns: columns,
              mobileColumns: columns,
            },
            position: 'midfielder',
          },
        ],
      },
      forwards: {
        data: [
          {
            name: positionLabels.forward,
            item: {
              data: filteredForwardPlayers as INormalizedTeamSquadItem,
              desktopColumns: columns,
              mobileColumns: columns,
            },
            position: 'forward',
          },
        ],
      },
    },
    id: teamSquad.id,
    teamSquadDataGridView: {
      keepers: { name: positionLabels.goalkeeper, data: keeperPlayers },
      defenders: { name: positionLabels.defender, data: defenderPlayers },
      midfielders: { name: positionLabels.midfielder, data: midfielderPlayers },
      forwards: { name: positionLabels.forward, data: forwardPlayer },
    },
    name: teamSquad?.name ? teamSquad?.name : NOT_AVAILABLE,
    team: squad.team,
    teamLink: linkService?.createEntityUrl(String(squad.team.id), TEAM_ENTITY, '', squad.team.slug) ?? '#',
    urlTarget: dataEntityLinks?.configuration?.newWindow ? TARGET_BLANK : TARGET_SELF,
    tournament: normalizeTournamentData({ tournament: teamSquad?.tournament, dataEntityLinks }),
  });
};
